import { CommonModule, NgFor } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import {
  Organization,
  OrganizationRider,
  OrganizationRiderDetail,
  OrganizationRiderPool,
  OrganizationService,
  OrganizationServiceLevel,
  Rider
} from '@uzurv/clara';
import { isNullOrUndefined, PermissionsConstants, StringValidators, UzurvDirectivesModule, UzurvPipesModule } from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';
import { MaterialModule } from 'projects/uzurv/src/app/material.module';
import * as OrganizationRiderActions from 'projects/uzurv/src/store/organization-rider/organization-rider.actions';
import { OrganizationRiderState } from 'projects/uzurv/src/store/organization-rider/organization-rider.reducer';
import { OrganizationRiderPoolUtil } from 'projects/uzurv/src/utils/organization-rider-pools.util';
import { firstValueFrom, map } from 'rxjs';
import { OrganizationSelectModule } from '../../organization-select';

export interface OrganizationRiderDialogData {
  uzurvRider: Rider;
  organizations: Array<any>;
  organizationRiderDetails: Array<OrganizationRiderDetail>;
}

interface AddOrganizationRiderForm {
  organization: FormControl<Organization | null>;
  partnerIdentifier: FormControl<string | null>;
  riderServiceLevelsFormArray: FormArray<any | null>;
  organizationRiderPool: FormControl<OrganizationRiderPool | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  emailAddress: FormControl<string | null>;
  phoneNumber: FormControl<string | null>;
  dateOfBirth: FormControl<Date | null>;
}

@Component({
  selector: 'app-add-organization-rider-dialog',
  templateUrl: './add-organization-rider-dialog.component.html',
  styleUrls: ['./add-organization-rider-dialog.component.scss'],
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    NgFor,
    ReactiveFormsModule,
    FormsModule,
    UzurvDirectivesModule,
    UzurvPipesModule,
    OrganizationSelectModule
  ]
})
export class AddOrganizationRiderDialogComponent implements OnInit {
  addOrganizationRiderForm!: FormGroup<AddOrganizationRiderForm>;

  organizationServiceLevels!: Array<OrganizationServiceLevel>;
  hasDuplicateOrganizations: any;

  organizationRiderPools: Array<OrganizationRiderPool> = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OrganizationRiderDialogData,
    private authDataService: AuthDataService,
    private dialogRef: MatDialogRef<AddOrganizationRiderDialogComponent>,
    private formBuilder: FormBuilder,
    private organizationService: OrganizationService,
    private store$: Store<State<OrganizationRiderState>>
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  /**
   * Create the main form for adding a new organization.
   * Pre-populates fields with data if applicable.
   */
  initForm(): void {
    const riderHydrationData: Rider = this.data.uzurvRider;

    if (!this.authDataService.hasPermission({ all: [PermissionsConstants.RIDER_READ] }) && this.data.organizationRiderDetails?.length > 0) {
      riderHydrationData.firstName = this.data.organizationRiderDetails[0].firstName;
      riderHydrationData.lastName = this.data.organizationRiderDetails[0].lastName;
      riderHydrationData.emailAddress = this.data.organizationRiderDetails[0].emailAddress;
      riderHydrationData.phoneNumber = this.data.organizationRiderDetails[0].phoneNumber;
      riderHydrationData.dateOfBirth = this.data.organizationRiderDetails[0].dateOfBirth;
    }

    this.addOrganizationRiderForm = new FormGroup<AddOrganizationRiderForm>({
      organization: new FormControl(null, [Validators.required, this.uniqueOrganizations.bind(this)]),
      partnerIdentifier: new FormControl(null, StringValidators.noSpecialCharacters()),
      riderServiceLevelsFormArray: new FormArray([]),
      organizationRiderPool: new FormControl(null),
      firstName: new FormControl(riderHydrationData?.firstName, Validators.required),
      lastName: new FormControl(riderHydrationData?.lastName, Validators.required),
      emailAddress: new FormControl(riderHydrationData?.emailAddress, Validators.email),
      phoneNumber: new FormControl(riderHydrationData?.phoneNumber),
      dateOfBirth: new FormControl(riderHydrationData?.dateOfBirth)
    });
  }

  get riderServiceLevelsFormArray(): FormArray {
    return this.addOrganizationRiderForm.controls.riderServiceLevelsFormArray as FormArray;
  }

  /**
   * Updates/fetches new organization service levels and organization rider pools on organization change.
   *
   * @param event
   */
  async handleOrganizationSelected(event: any) {
    // Clearing the field results in no id being present, only continue if id is not null.
    this.hasDuplicateOrganizations = this.data.organizationRiderDetails.some(
      organizationRiderDetail => organizationRiderDetail.organizationId === event.id
    );

    this.organizationService
      .listOrganizationServiceLevels(event.id)
      .pipe(
        map(osl => {
          this.organizationServiceLevels = osl;
          const filteredOrganizationServiceLevels = this.organizationServiceLevels.filter(osl => osl.optional);

          this.riderServiceLevelsFormArray.clear();
          if (filteredOrganizationServiceLevels.length > 0) {
            filteredOrganizationServiceLevels.forEach(organizationServiceLevel => {
              this.riderServiceLevelsFormArray.push(this.createServiceLevelFormGroup(organizationServiceLevel));
            });
          }
        })
      )
      .subscribe();

    // Retrieve an array of available rider pools.
    this.organizationRiderPools = await firstValueFrom(this.organizationService.listRiderPoolsByOrganizationId(event.id));

    // Ensure organization rider pool form field is in the correct state.
    this.updateOrganizationRiderPoolFormField();
  }

  /**
   * Creates a separate form group for organization service level.
   *
   * @param organizationServiceLevel
   * @returns
   */
  createServiceLevelFormGroup(organizationServiceLevel: OrganizationServiceLevel): FormGroup {
    return this.formBuilder.group({
      serviceLevel: [organizationServiceLevel.serviceLevel],
      enabled: [organizationServiceLevel.optional ? null : true]
    });
  }

  /**
   * Handles changing organization service level.
   *
   * @param index
   * @param event
   */
  changeServiceLevel(index: number, event: any): void {
    const serviceLevelFormGroup = this.riderServiceLevelsFormArray.at(index) as FormGroup;
    serviceLevelFormGroup.patchValue({ enabled: event.value });
  }

  /**
   * Handles display of the organization rider pool selection field.
   *
   * @param organizationId
   * @returns {boolean}
   */
  shouldShowOrganizationRiderPoolSelection(organizationId: number): boolean {
    return OrganizationRiderPoolUtil.shouldShowOrganizationRiderPoolSelection(this.organizationRiderPools, organizationId);
  }

  /**
   * Returns a string based on given organizationRiderPool ride limit information.
   *
   * @param organizationRiderPool
   * @returns {string}
   */
  getOrganizationRiderPoolDisplayText(organizationRiderPool: OrganizationRiderPool): string {
    return OrganizationRiderPoolUtil.getOrganizationRiderPoolDisplayTextViaPool(organizationRiderPool);
  }

  /**
   * Handles submitting the form.
   * Creates Rider and OrganizationRider objects and passes those objects to store along with Organization and OrganizationRiderPool.
   */
  handleSubmission(): void {
    const rider = new Rider();

    Object.assign(rider, {
      firstName: this.addOrganizationRiderForm.controls.firstName?.value,
      lastName: this.addOrganizationRiderForm.controls.lastName?.value,
      emailAddress: this.addOrganizationRiderForm.controls.emailAddress?.value,
      phoneNumber: this.addOrganizationRiderForm.controls.phoneNumber?.value,
      dateOfBirth: this.addOrganizationRiderForm.controls.dateOfBirth?.value,
      id: this.data.uzurvRider.id
    });

    const organizationRider = new OrganizationRider();

    Object.assign(organizationRider, {
      firstName: this.addOrganizationRiderForm.controls.firstName?.value,
      lastName: this.addOrganizationRiderForm.controls.lastName?.value,
      emailAddress: this.addOrganizationRiderForm.controls.emailAddress?.value,
      phoneNumber: this.addOrganizationRiderForm.controls.phoneNumber?.value,
      dateOfBirth: this.addOrganizationRiderForm.controls.dateOfBirth?.value,
      organizationId: this.addOrganizationRiderForm.controls.organization.value.id,
      partnerIdentifier: this.addOrganizationRiderForm.controls.partnerIdentifier?.value,
      organizationRiderPool: this.addOrganizationRiderForm.controls.organizationRiderPool?.value,
      rider: rider
    });

    this.store$.dispatch(
      OrganizationRiderActions.CreateOrganizationRider({
        payload: {
          organizationRider: organizationRider,
          organization: this.addOrganizationRiderForm.controls.organization?.value,
          organizationRiderServiceLevels: this.riderServiceLevelsFormArray.value,
          forNewItinerary: false,
          forAddingOrganizationRider: true
        }
      })
    );

    this.dialogRef.close();
  }

  /**
   * Throws error if the rider is already a part of the organization being added.
   */
  private uniqueOrganizations(): ValidationErrors | null {
    if (this.hasDuplicateOrganizations) {
      return { duplicateOrganizations: true };
    }

    return null;
  }

  /**
   * Performs checks to ensure organization rider pool form field is in the proper state with proper validation.
   */
  private updateOrganizationRiderPoolFormField(): void {
    // New organization selected, reset the form field.
    this.addOrganizationRiderForm.controls.organizationRiderPool.reset();

    // If the organization has no available rider pools, clear any validation on the field as it's not required.
    if (isNullOrUndefined(this.organizationRiderPools) || this.organizationRiderPools.length === 0) {
      this.addOrganizationRiderForm.controls.organizationRiderPool.clearValidators();
    } else {
      // If the organization has only one rider pool...
      if (this.organizationRiderPools.length === 1) {
        // If the singular rider pool has no ride limit, clear any validation on the field.
        if (isNullOrUndefined(this.organizationRiderPools[0].rideLimit)) {
          this.addOrganizationRiderForm.controls.organizationRiderPool.clearValidators();
        } else {
          // If the singular rider pool has a ride limit, set the field to required, set the value to be that pool, disable editing the field.
          this.addOrganizationRiderForm.controls.organizationRiderPool.setValidators([Validators.required]);
          this.addOrganizationRiderForm.controls.organizationRiderPool.setValue(this.organizationRiderPools[0]);
          this.addOrganizationRiderForm.controls.organizationRiderPool.disable();
        }
      } else {
        // If the organization has multiple rider pools, set the field to required and enable editing.
        this.addOrganizationRiderForm.controls.organizationRiderPool.enable();
        this.addOrganizationRiderForm.controls.organizationRiderPool.setValidators([Validators.required]);
      }
    }

    // Update the value and validity for the field.
    this.addOrganizationRiderForm.controls.organizationRiderPool.updateValueAndValidity();
  }
}
